/**
 * services/socialApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Social interaction methods — likes, follows, seller profiles.
 *
 * METHODS:
 *   toggleAdLike(adId)             — like / unlike an ad
 *   getAdLikeStatus(adId)          — check if current user has liked an ad
 *   toggleFollow(sellerEmail)      — follow / unfollow a seller
 *   getFollowStatus(sellerEmail)   — check follow status + follower count
 *   getSellerProfile(emailOrId)    — fetch a seller's public profile + stats
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL, resolveAvatarUrl } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async toggleAdLike(adId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/like/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Failed to toggle like' };
      }
      const data = await res.json();
      return { success: true, is_liked: data.is_liked, like_count: data.like_count };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async getAdLikeStatus(adId) {
    try {
      const token = this._getToken();
      const headers = token ? { 'Authorization': `Token ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/like/status/`, { headers });
      if (!res.ok) throw new Error('Failed to get like status');
      const data = await res.json();
      return { is_liked: data.is_liked, like_count: data.like_count };
    } catch { return { is_liked: false, like_count: 0 }; }
  },
// USER FOLLOWS

  async toggleFollow(sellerEmail) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(
        `${API_BASE_URL}/users/${encodeURIComponent(sellerEmail)}/follow/`,
        { method: 'POST', headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Failed to toggle follow' };
      }
      const data = await res.json();
      return { success: true, is_following: data.is_following, follower_count: data.follower_count };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async getFollowStatus(sellerEmail) {
    try {
      const token = this._getToken();
      const headers = token ? { 'Authorization': `Token ${token}` } : {};
      const res = await fetch(
        `${API_BASE_URL}/users/${encodeURIComponent(sellerEmail)}/follow/status/`,
        { headers }
      );
      if (!res.ok) throw new Error('Failed to get follow status');
      const data = await res.json();
      return { is_following: data.is_following, follower_count: data.follower_count };
    } catch { return { is_following: false, follower_count: 0 }; }
  },

  // ✅ NEW: SELLER PROFILE — public avatar lookup
  // Used by ProductInfoSection as a fallback when the ad doesn't already
  // carry seller_profile_image_url (e.g. cached ad objects from before
  // the serializer was updated, or older API responses).
  //
  // Endpoint: GET /api/users/seller-profile/?email=<sellerEmail>
  // Response: { first_name, last_name, seller_name, profile_image_url },
// No auth required — public endpoint.

  async getSellerProfile(emailOrIdentifier) {
    try {
      if (!emailOrIdentifier) return null;
      const url = `${API_BASE_URL}/users/seller-profile/?email=${encodeURIComponent(emailOrIdentifier)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`getSellerProfile failed: ${res.status}`);
      const data = await res.json();
      // Resolve relative URLs to absolute
      if (data.profile_image_url) {
        data.profile_image_url = resolveAvatarUrl(data.profile_image_url);
      }
      return data;
    } catch (error) {
      // Non-fatal — ProductInfoSection will fall back to initials avatar
      console.warn('⚠️ getSellerProfile error (non-fatal):', error.message);
      return null;
    }
  }

  // OFFERS
};

